import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import {
  Accordion,
  Col,
  KeyValue,
  NoValue,
  Row,
  TextLink,
} from '@folio/stripes/components';
import { ViewMetaData } from '@folio/stripes/smart-components';

import FolioFormattedDate from '../../../../common/folio-formatted-date';
import { getTeamViewUrl } from '../../../../../constants';

const GeneralInformation = ({
  task: {
    name,
    status,
    priority,
    startDate,
    dueDate,
    description,
    metadata,
    assignedUserName,
    team,
  }
}) => {
  const renderTeamLink = () => {
    const {
      id,
      name: teamName,
    } = team;

    if (id) {
      return (
        <TextLink
          id="team-link"
          to={getTeamViewUrl(id)}
        >
          {teamName}
        </TextLink>
      );
    }

    return <NoValue />;
  };

  return (
    <Accordion
      id="general"
      label={<FormattedMessage id="ui-task-list.title.accordion.generalInformation" />}
    >
      {metadata && (
        <Row>
          <Col sm={12}>
            <ViewMetaData
              id="metadata"
              metadata={metadata}
            />
          </Col>
        </Row>
      )}
      <Row>
        <Col sm={3}>
          <KeyValue
            data-test-task-details-name
            label={<FormattedMessage id="ui-task-list.task.field.name" />}
            value={name}
          />
        </Col>
        <Col sm={3}>
          <KeyValue
            data-test-task-details-status
            label={<FormattedMessage id="ui-task-list.task.field.status" />}
            value={<FormattedMessage id={`ui-task-list.task.statuses.${status}`} />}
          />
        </Col>
        <Col sm={3}>
          <KeyValue
            data-test-task-details-priority
            label={<FormattedMessage id="ui-task-list.task.field.priority" />}
            value={<FormattedMessage id={`ui-task-list.priority.${priority}`} />}
          />
        </Col>
        <Col sm={3}>
          <KeyValue
            data-test-task-details-assigned-user-name
            label={<FormattedMessage id="ui-task-list.task.field.assignedUserName" />}
            value={assignedUserName || <NoValue />}
          />
        </Col>
      </Row>

      <Row>
        <Col sm={3}>
          <KeyValue
            data-test-task-details-start-date
            label={<FormattedMessage id="ui-task-list.task.field.startDate" />}
            value={startDate ? <FolioFormattedDate value={startDate} /> : <NoValue />}
          />
        </Col>
        <Col sm={3}>
          <KeyValue
            data-test-task-details-due-date
            label={<FormattedMessage id="ui-task-list.task.field.dueDate" />}
            value={dueDate ? <FolioFormattedDate value={dueDate} /> : <NoValue />}
          />
        </Col>
        <Col sm={3}>
          <KeyValue
            data-test-task-details-assigned-team
            label={<FormattedMessage id="ui-task-list.task.field.assignedTeam" />}
            value={renderTeamLink()}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={12}>
          <KeyValue
            data-test-task-details-description
            label={<FormattedMessage id="ui-task-list.task.field.description" />}
            value={description || <NoValue />}
          />
        </Col>
      </Row>
    </Accordion>
  );
};

GeneralInformation.propTypes = {
  task: PropTypes.shape({
    assignedUserName: PropTypes.string,
    description: PropTypes.string,
    dueDate: PropTypes.string,
    metadata: PropTypes.object.isRequired,
    name: PropTypes.string.isRequired,
    priority: PropTypes.string.isRequired,
    startDate: PropTypes.string,
    status: PropTypes.string.isRequired,
    team: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string
    })
  })
};

GeneralInformation.defaultProps = {
  task: PropTypes.shape({
    assignedUserName: '',
    description: '',
    dueDate: '',
    startDate: '',
    team: {}
  })
};

export default GeneralInformation;
